import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Stethoscope, Shield, MessageSquare, Clock, Award,
  Heart, Activity, Sparkles, ChevronRight, Watch, Lock, Star,
  Home, HelpCircle, Users, BookOpen, Download, Share, CheckCircle2, Loader2, LogIn
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { AnimatedLogo, LoadingScreen } from "@/components/ui/animated-logo";
import { BottomNav } from "@/components/navigation/BottomNav";
import { TryMyCareHomeBanner } from "@/components/tryMycare/TryMyCareHomeBanner";
import logo from "@/assets/ilajly-logo-icon.png";

// Offer end date: March 31, 2026
const OFFER_END_DATE = new Date("2026-03-31T23:59:59");

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const now = new Date();
    const diff = targetDate.getTime() - now.getTime();
    return diff > 0 ? diff : 0;
  });

  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      setTimeLeft(diff > 0 ? diff : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate, timeLeft]);

  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, isExpired: timeLeft <= 0 };
}

const WEARABLES = [
  { name: "Apple Health", icon: "🍎" },
  { name: "Samsung Health", icon: "📱" },
  { name: "Google Fit", icon: "🏃" },
  { name: "Fitbit", icon: "⌚" },
  { name: "Garmin", icon: "🎯" },
  { name: "Mi Band", icon: "💪" },
];

/* ─── Install App Hook ─── */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function useInstallApp() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches
    || (navigator as any).standalone === true;

  useEffect(() => {
    if (isStandalone) { setIsInstalled(true); return; }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => { setIsInstalled(true); setIsInstalling(false); setDeferredPrompt(null); });
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone]);

  const install = async () => {
    if (deferredPrompt) {
      setIsInstalling(true);
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          setIsInstalled(true);
        }
      } catch {
        // prompt failed silently
      } finally {
        setIsInstalling(false);
        setDeferredPrompt(null);
      }
    } else if (isIOS) {
      setShowIOSDialog(true);
    }
  };

  const canShow = !isInstalled && !isStandalone;

  return { install, canShow, isInstalled, isInstalling, isIOS, showIOSDialog, setShowIOSDialog, hasNativePrompt: !!deferredPrompt };
}

/* ─── iOS Install Instructions Dialog ─── */
function IOSInstallDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-foreground">Install iLAJLY on iPhone</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Follow these steps to add iLAJLY to your home screen:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">1</span>
            </div>
            <p className="text-sm text-foreground">
              Tap the <Share className="inline w-4 h-4 text-primary" /> <strong>Share</strong> button in Safari's toolbar
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">2</span>
            </div>
            <p className="text-sm text-foreground">
              Scroll down and tap <strong>"Add to Home Screen"</strong>
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">3</span>
            </div>
            <p className="text-sm text-foreground">
              Tap <strong>"Add"</strong> — iLAJLY will appear on your home screen
            </p>
          </div>
        </div>
        <Button onClick={onClose} className="w-full">Got it</Button>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Tab: Home ─── */
function HomeTab({ countdown, installApp }: { countdown: ReturnType<typeof useCountdown>; installApp: ReturnType<typeof useInstallApp> }) {
  return (
    <div className="px-4 py-6 space-y-8">
      {/* Hero */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
          <Sparkles className="w-4 h-4 text-primary animate-pulse" />
          <span className="text-sm font-medium text-foreground">Real doctors. Real care.</span>
        </div>
        <h1 className="text-3xl font-display font-bold text-foreground leading-tight">
          Talk to Real{" "}
          <span className="text-primary">AYUSH Doctors</span>
        </h1>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          Connect with verified AYUSH practitioners when it truly matters. Your privacy is protected, your care is real.
        </p>
      </div>

      {/* Install App Button */}
      {installApp.canShow && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Button
            size="lg"
            className="w-full rounded-xl py-6 text-base bg-[hsl(160,84%,20%)] text-white hover:bg-[hsl(160,84%,16%)] shadow-md"
            onClick={installApp.install}
            disabled={installApp.isInstalling}
          >
            {installApp.isInstalling ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Installing…
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Install iLAJLY App
              </>
            )}
          </Button>
        </motion.div>
      )}
      {installApp.isInstalled && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex items-center justify-center gap-2 text-sm text-primary font-medium py-3"
        >
          <CheckCircle2 className="w-5 h-5" /> App Installed
        </motion.div>
      )}

      {/* CTA Buttons */}
      <div className="flex flex-col gap-3">
        <Button asChild size="lg" className="w-full rounded-xl py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/auth">
            <LogIn className="w-5 h-5 mr-2" />
            Login
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="w-full rounded-xl py-6 text-base border-2 border-primary text-primary hover:bg-primary/5">
          <Link to="/auth/patient">
            <User className="w-5 h-5 mr-2" />
            Patient Register
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Link>
        </Button>
        <Button asChild size="lg" className="w-full rounded-xl py-6 text-base bg-muted text-foreground hover:bg-muted/80 border border-border">
          <Link to="/auth/doctor">
            <Stethoscope className="w-5 h-5 mr-2" />
            Doctor Register
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Link>
        </Button>
      </div>

      {/* Doctor Free Registration Offer */}
      {!countdown.isExpired && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 text-center space-y-2">
            <div className="flex items-center justify-center gap-2">
              <span className="text-xl font-bold text-primary">FREE</span>
              <span className="text-sm text-muted-foreground line-through">₹3,499</span>
              <Badge className="bg-primary/20 text-primary border-0 text-[10px]">100% OFF</Badge>
            </div>
            <p className="text-xs text-muted-foreground">Doctor registration free till March 31, 2026</p>
            <div className="flex justify-center gap-1">
              {[
                { v: countdown.days, l: "days" },
                { v: countdown.hours, l: "hrs" },
                { v: countdown.minutes, l: "min" },
                { v: countdown.seconds, l: "sec" },
              ].map((t) => (
                <div key={t.l} className="bg-background px-2 py-1 rounded text-center min-w-[36px]">
                  <span className="text-xs font-bold text-foreground">{t.v}</span>
                  <span className="text-[8px] text-muted-foreground block">{t.l}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Try My Care Banner */}
      <TryMyCareHomeBanner />

      {/* Platform Features */}
      <div className="space-y-4">
        <h2 className="text-lg font-display font-semibold text-foreground text-center">Why ilajly?</h2>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: "100%", label: "Anonymous", icon: Shield },
            { value: "Verified", label: "Doctors", icon: Award },
            { value: "Chat Only", label: "Sessions", icon: MessageSquare },
            { value: "₹1 = 1", label: "Credit", icon: Star },
          ].map((stat) => (
            <div key={stat.label} className="text-center p-4 rounded-2xl bg-card border border-border">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-primary/10 flex items-center justify-center">
                <stat.icon className="w-5 h-5 text-primary" />
              </div>
              <div className="text-lg font-bold text-primary">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Wearable Sync */}
      <Card className="border-primary/10">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Watch className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Wearable Health Sync</h3>
              <Badge variant="secondary" className="text-[10px]">Coming Soon</Badge>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Sync health data from smart devices. Doctors see real-time vitals during consultations.
          </p>
          <div className="flex flex-wrap gap-2">
            {WEARABLES.map((d) => (
              <span key={d.name} className="text-xs bg-muted px-2 py-1 rounded-lg">{d.icon} {d.name}</span>
            ))}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="w-3 h-3 text-primary" />
            Read-only access • You control what's shared
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Tab: How It Works ─── */
function HowItWorksTab() {
  const steps = [
    { icon: User, title: "Create Account", desc: "Sign up with email. Your identity stays anonymous during consultations." },
    { icon: Star, title: "Buy Credits", desc: "₹1 = 1 Credit. Choose a plan that fits your needs. Credits power your consultations." },
    { icon: Stethoscope, title: "Find a Doctor", desc: "Search verified AYUSH practitioners by speciality, location, or availability." },
    { icon: MessageSquare, title: "Chat Consultation", desc: "Consult via secure in-app chat. No phone calls, no identity exposure." },
    { icon: Heart, title: "Track Your Health", desc: "Use your private health diary to log symptoms and track wellbeing over time." },
  ];

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-display font-bold text-foreground">How ilajly Works</h2>
        <p className="text-sm text-muted-foreground">Simple, secure, anonymous healthcare</p>
      </div>

      <div className="space-y-4">
        {steps.map((step, i) => (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="flex gap-4 items-start"
          >
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                {i + 1}
              </div>
              {i < steps.length - 1 && <div className="w-0.5 h-8 bg-primary/20 mt-1" />}
            </div>
            <div className="flex-1 pb-4">
              <h3 className="font-semibold text-foreground text-sm">{step.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{step.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold text-foreground text-sm">Key Highlights</h3>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-primary flex-shrink-0" /> Anonymous consultations — doctor never sees your real name</li>
            <li className="flex items-center gap-2"><Award className="w-4 h-4 text-primary flex-shrink-0" /> All doctors are verified AYUSH practitioners</li>
            <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary flex-shrink-0" /> Chat-based only — documented and secure</li>
            <li className="flex items-center gap-2"><Star className="w-4 h-4 text-primary flex-shrink-0" /> Simple credit system: ₹1 = 1 Credit</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Tab: For Doctors ─── */
function DoctorsInfoTab({ countdown }: { countdown: ReturnType<typeof useCountdown> }) {
  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
          <Stethoscope className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-display font-bold text-foreground">For Doctors</h2>
        <p className="text-sm text-muted-foreground">Join our verified practitioner network</p>
      </div>

      <div className="space-y-3">
        {[
          { icon: Award, title: "Verified Credentials", desc: "Get verified once and gain patient trust instantly." },
          { icon: Users, title: "Patient Consultations", desc: "Receive consultation requests from patients across India." },
          { icon: Clock, title: "Set Your Fees", desc: "Define your own consultation rates and availability." },
          { icon: Activity, title: "Patient Insights", desc: "Access shared health diaries and wearable data for better care." },
        ].map((item) => (
          <Card key={item.title} className="border-border">
            <CardContent className="p-4 flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!countdown.isExpired && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 text-center space-y-2">
            <div className="flex items-center justify-center gap-2">
              <span className="text-xl font-bold text-primary">FREE Registration</span>
              <span className="text-sm text-muted-foreground line-through">₹3,499</span>
            </div>
            <p className="text-xs text-muted-foreground">Limited offer till March 31, 2026</p>
          </CardContent>
        </Card>
      )}

      <Button asChild size="lg" className="w-full rounded-xl py-6 bg-primary hover:bg-primary/90">
        <Link to="/auth/doctor">
          Join as Doctor
          <ChevronRight className="w-4 h-4 ml-2" />
        </Link>
      </Button>
    </div>
  );
}

/* ─── Tab: For Patients ─── */
function PatientsInfoTab() {
  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
          <User className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-display font-bold text-foreground">For Patients</h2>
        <p className="text-sm text-muted-foreground">Your health, your privacy, your control</p>
      </div>

      <div className="space-y-3">
        {[
          { icon: Shield, title: "Anonymous Consultations", desc: "Your real name and contact are never shared with the doctor." },
          { icon: MessageSquare, title: "Secure Messaging", desc: "Chat with verified doctors inside the app. No phone calls needed." },
          { icon: Heart, title: "Health Diary", desc: "Log symptoms, mood, and notes privately. Share with doctors only when you choose." },
          { icon: Watch, title: "Wearable Health Sync", desc: "Connect your smartwatch or fitness band to share real-time vitals with your doctor." },
        ].map((item) => (
          <Card key={item.title} className="border-border">
            <CardContent className="p-4 flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button asChild size="lg" className="w-full rounded-xl py-6 bg-primary hover:bg-primary/90">
        <Link to="/auth/patient">
          Start Consulting
          <ChevronRight className="w-4 h-4 ml-2" />
        </Link>
      </Button>
    </div>
  );
}

/* ─── Tab: FAQ ─── */
function FAQTab() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const faqs = [
    { q: "What is ilajly?", a: "ilajly is a platform that connects patients with verified AYUSH doctors (Homoeopathy, Ayurveda, Siddha, Unani, Yoga & Naturopathy) for anonymous, chat-based consultations." },
    { q: "Are the doctors verified?", a: "Yes. Every doctor on ilajly goes through a credential verification process before they can accept consultations." },
    { q: "Is ilajly AI or real doctors?", a: "ilajly connects you with real, verified AYUSH practitioners. There are no AI chatbots. Every consultation is with a human doctor." },
    { q: "Is my data safe?", a: "Absolutely. Consultations are anonymous by default. Your real identity is never shared with the doctor unless you explicitly consent." },
    { q: "How does the credit system work?", a: "₹1 = 1 Credit. You purchase credits through plans, and credits are used to pay for consultations. Simple and transparent." },
    { q: "Can I choose my doctor?", a: "Yes. You can search doctors by speciality, location, and availability, and send a consultation request to the doctor of your choice." },
    { q: "What if I need to visit a clinic?", a: "If a doctor recommends an in-person visit, location details can be shared only with your explicit consent." },
  ];

  return (
    <div className="px-4 py-6 space-y-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-display font-bold text-foreground">Frequently Asked Questions</h2>
        <p className="text-sm text-muted-foreground">Everything you need to know</p>
      </div>

      <div className="space-y-2">
        {faqs.map((faq, i) => (
          <Card key={i} className="border-border overflow-hidden">
            <button
              className="w-full p-4 text-left flex items-center justify-between"
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
            >
              <span className="font-medium text-foreground text-sm pr-4">{faq.q}</span>
              <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${openIdx === i ? "rotate-90" : ""}`} />
            </button>
            <AnimatePresence>
              {openIdx === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 text-xs text-muted-foreground">{faq.a}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Index Component ─── */
const NAV_ITEMS = [
  { icon: Home, label: "Home", value: "home" },
  { icon: BookOpen, label: "How It Works", value: "how" },
  { icon: Stethoscope, label: "Doctors", value: "doctors" },
  { icon: User, label: "Patients", value: "patients" },
  { icon: HelpCircle, label: "FAQ", value: "faq" },
];

const Index = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const countdown = useCountdown(OFFER_END_DATE);
  const navigate = useNavigate();
  const installApp = useInstallApp();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
  }, []);

  return (
    <>
      <AnimatePresence>
        {isLoading && <LoadingScreen />}
      </AnimatePresence>

      <div className="min-h-screen bg-background flex flex-col">
        {/* Fixed Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-lg border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center h-14">
              <img src={logo} alt="ilajly" className="h-8 w-auto" width={32} height={32} />
              <span className="font-display font-semibold text-foreground ml-2">ilajly</span>
            </div>
          </div>
        </header>

        {/* Continue to Dashboard Banner for logged-in users */}
        {isLoggedIn && (
          <div className="sticky top-14 z-40 bg-primary text-primary-foreground">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium"
            >
              <ChevronRight className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        )}

        <main className="flex-1 pb-20 overflow-y-auto max-w-5xl mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
            >
              {activeTab === "home" && <HomeTab countdown={countdown} installApp={installApp} />}
              {activeTab === "how" && <HowItWorksTab />}
              {activeTab === "doctors" && <DoctorsInfoTab countdown={countdown} />}
              {activeTab === "patients" && <PatientsInfoTab />}
              {activeTab === "faq" && <FAQTab />}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Bottom Navigation */}
        <BottomNav items={NAV_ITEMS} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* iOS Install Instructions */}
      <IOSInstallDialog open={installApp.showIOSDialog} onClose={() => installApp.setShowIOSDialog(false)} />
    </>
  );
};

export default Index;
